import React, { useRef, useState } from 'react';
import Modal from 'react-responsive-modal';
import { Button } from '@synergy-marine-group/synergy-ui';
import uploadVesselCostCentre from './queries/uploadVesselCostCentre';
import { validateCostCentreCsv } from './validateVesselCostCentreCsv';
import showAlert from '../../utils/alert';
import { deleteModalStyles } from '../Modal/styles';

const COST_CENTRE_TOAST_CLASS = 'cost-centre-toast-multiline';
const COST_CENTRE_TOAST_BODY_CLASS = 'cost-centre-toast-multiline-body';

const getCostCentreToastOptions = (message) => {
  const text = String(message || '');
  if (!text.includes('\n') && !text.includes('\r')) {
    return undefined;
  }
  return {
    className: COST_CENTRE_TOAST_CLASS,
    bodyClassName: COST_CENTRE_TOAST_BODY_CLASS,
    containerId: 'cost-centre-toast',
    autoClose: 10000
  };
};

const normalizeBackendErrors = (errors = []) => {
  const list = [];
  (Array.isArray(errors) ? errors : []).forEach((err) => {
    const text = typeof err === 'string' ? err : err?.message;
    if (text && String(text).trim()) list.push(String(text).trim());
  });
  return Array.from(new Set(list));
};

const getAlertType = (message, isSuccess, status) => {
  if (!isSuccess && status >= 400) return 'error';
  if (/already available|updated again|warning/i.test(String(message || ''))) return 'warning';
  return isSuccess || status < 400 ? 'success' : 'error';
};

const getBackendUploadMessage = (data) => (
  typeof data?.message === 'string'
    ? data.message.trim().replace(/\r\n/g, '\n')
    : ''
);

const UploadCostCentreModal = ({ open, setOpen }) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [clientErrors, setClientErrors] = useState([]);
  const {
    mutate: uploadFile,
    isLoading: isUploading,
    reset: resetUpload
  } = uploadVesselCostCentre();

  const resetState = () => {
    setSelectedFile(null);
    setClientErrors([]);
    resetUpload();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetState();
    setOpen(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setClientErrors([]);
    if (!file) {
      setSelectedFile(null);
      return;
    }
    const name = file.name?.toLowerCase() || '';
    if (!name.endsWith('.csv')) {
      setSelectedFile(null);
      setClientErrors(['Please select a valid CSV file.']);
      return;
    }
    setSelectedFile(file);
  };

  const parseResponseData = (raw) => {
    if (raw == null) return {};
    if (typeof raw === 'object' && !(raw instanceof Blob)) return raw;
    if (typeof raw === 'string') {
      const text = raw.trim();
      if (!text) return {};
      try {
        return JSON.parse(text);
      } catch (e) {
        return { message: text };
      }
    }
    return {};
  };

  const handleUploadResult = (response) => {
    const data = parseResponseData(response?.data);
    const status = Number(response?.status);
    const newCount = Number(data?.newCount ?? data?.addedCount ?? data?.createdCount ?? 0);
    const updatedCount = Number(data?.updatedCount ?? 0);
    const updatedItems = data?.updatedItems || data?.duplicates || data?.alreadyAvailable || [];
    const hasUpdates = Array.isArray(updatedItems) && updatedItems.length > 0;
    const backendErrors = normalizeBackendErrors(data?.errors || data?.errorMessages || []);
    const backendMessage = getBackendUploadMessage(data);
    const isSuccess = data?.isSuccess === true
      || newCount > 0
      || updatedCount > 0
      || hasUpdates
      || (status >= 200 && status < 300 && backendErrors.length === 0);

    // Show only backend messages (no frontend-constructed success/warning text)
    if (backendErrors.length > 0) {
      setClientErrors(backendErrors);
      backendErrors.forEach((err) => showAlert(err, 'error'));
    }

    if (backendMessage) {
      const alreadyShown = backendErrors.some(
        (err) => err === backendMessage || backendMessage.includes(err)
      );
      if (!alreadyShown) {
        showAlert(
          backendMessage,
          getAlertType(backendMessage, isSuccess, status),
          getCostCentreToastOptions(backendMessage)
        );
      }
    }

    if (isSuccess) {
      handleClose();
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      setClientErrors(['Please select a CSV file to upload.']);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const { errors, csvBlob } = validateCostCentreCsv(reader.result);
      if (errors.length > 0) {
        setClientErrors(errors);
        errors.forEach((err) => showAlert(err, 'error'));
        return;
      }

      // Always upload regenerated CSV with backend template headers
      const trimmedFile = new File(
        [csvBlob],
        selectedFile.name || 'Vessel_Cost_Centre_Template.csv',
        { type: 'text/csv' }
      );

      uploadFile(trimmedFile, {
        onSuccess: (response) => handleUploadResult(response),
        onError: (error) => {
          if (!error?.response) {
            showAlert('Something went wrong while uploading cost centers', 'error');
            return;
          }
          const data = parseResponseData(error.response.data);
          if (
            data
            && (data.isSuccess != null
              || data.newCount != null
              || data.updatedCount != null
              || Array.isArray(data.errors)
              || data.message)
          ) {
            handleUploadResult({ data, status: error.response.status });
            return;
          }
          if (typeof error.response.data === 'string' && error.response.data.trim()) {
            setClientErrors([error.response.data]);
            showAlert(error.response.data, 'error');
            return;
          }
          showAlert('Something went wrong while uploading cost centers', 'error');
        }
      });
    };
    reader.onerror = () => {
      showAlert('Failed to read the selected file', 'error');
    };
    reader.readAsText(selectedFile);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeOnOverlayClick={false}
      center
      showCloseIcon={false}
      styles={deleteModalStyles}
    >
      <div className="w-auto py-8 px-8 font-muli" style={{ minWidth: '28rem' }}>
        <h2 className="uppercase text-center pb-6 font-montserrat font-bold">
          Upload Cost Centres
        </h2>

        <div className="text-left pb-2 text-sm">
          CSV columns: SapCompanyCode, SapVesselCompanyCode, CostCenter
        </div>
        <div className="text-left pb-4 text-xs text-gray-500">
          Use valid SAP company / vessel codes from master data (example: 5430, D000, D000-WHAL1).
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileChange}
          className="block w-full text-sm mb-4"
        />

        {clientErrors.length > 0 && (
          <div className="text-left text-sm text-red-500 pb-4 max-h-40 overflow-auto">
            {clientErrors.map((err) => (
              <div key={err}>{err}</div>
            ))}
          </div>
        )}

        <div className="flex justify-between pt-2">
          <Button
            className="max-w-none font-montserrat text-gray-500 uppercase text-sm"
            onClick={handleClose}
            size="large"
            variant="outlined"
            rounded
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            className="max-w-none font-montserrat text-gray-500 uppercase text-sm"
            onClick={handleUpload}
            size="large"
            variant=" filled"
            rounded
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UploadCostCentreModal;
